import React, { useContext, useEffect, useRef, useState } from 'react';

import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import LazyLoading from '../../../components/LazyLoading';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';

import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';

import { Society } from '../../../app/models/Society';
import { Provider } from '../../../app/models/Provider';
import { useHistory } from 'react-router-dom';
import { Truck, TruckDataForm, defaultTruckDataForm } from '../../../app/models/Truck';
import useTruckService from '../../../app/services/hooks/useTruckService';
import { Brand } from '../../../app/models/Brand';
import { VehicleModel } from '../../../app/models/VehicleModel';
import { WorkOrderDataForm, defaultWorkOrder } from '../../../app/models/WorkOrder';
import useWorkOrderService from '../../../app/services/hooks/useWorkOrderService';
import WorkOrderForm from './forms/WorkOrderForm';
import useNavigationPage from '../../../hooks/useNavigationPage';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const WorkOrderCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const history = useHistory();

    const { createWorkOrder, storeWorkOrder, fetchingCreateWorkOrder, fetchingStoreWorkOrder } =
        useWorkOrderService();
    const [societies, setSocieties] = useState<Society[]>([]);
    const [trucks, setTrucks] = useState<Truck[]>([]);
    const [workOrder, setWorkOrder] = useState<WorkOrderDataForm>(defaultWorkOrder);

    const [errorFields, setErrorFields] = useState<any>();
    const { navigationPage } = useNavigationPage();
    const confirmRef = useRef<boolean>(false);

    useEffect(() => {
        if (showLoading) showLoading('loading', 'Cargando datos...');

        createWorkOrder({
            onSuccess: (response: ServiceResponse) => {
                setSocieties(response.data.societies);
                setTrucks(response.data.trucks);

                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        });
    }, []);

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando orden de trabajo...');
        storeWorkOrder({
            ...workOrder,
            confirm: confirmRef.current
        }, {
            onSuccess: (response: ServiceResponse) => {
                setErrorFields(undefined);
                if (hideLoading) hideLoading();
                if (response.data?.validate) {
                    confirmStore(response.message);
                } else {
                    useReactConfirmAlert().successAlert({
                        title: 'Éxito',
                        message: response.message
                    });
                    navigationPage('/work-orders/' + response.data.work_order_id + '/edit');
                    toast.success(response.message, {
                        autoClose: 2500
                    });

                    confirmRef.current = false;
                    if (onSaved) onSaved();
                }
            
            },
            onError: (response: ServiceResponse) => {
                confirmRef.current = false;
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                confirmRef.current = false;
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const confirmStore = (message: string) => {
        const _text = message;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        confirmRef.current = true;
                        store();
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        confirmRef.current = false;
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La orden de transporte no se ha creado.'
                            });
                        }, 0);
                    }
                }
            },
            onClickOutsideFunction: () => {
                confirmRef.current = false;
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    // if (errorGetData !== '') {
    //     return <div>{errorGetData}</div>;
    // }

    return fetchingCreateWorkOrder ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <div className="row">
                <div className="col-12">
                    <WorkOrderForm
                        workOrderForm={workOrder}
                        setWorkOrderForm={setWorkOrder}
                        societies={societies}
                        trucks={trucks}
                        errorFields={errorFields}
                    />
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} locked={fetchingStoreWorkOrder} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStoreWorkOrder} />
                </div>
            </div>
        </>
    );
};

export default WorkOrderCreate;
